import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

// ── Client Profile Modal ──────────────────────────────────────────────────────
function ClientProfile({ client, onClose }) {
  const [subscriptions, setSubscriptions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);
  const [editingBilling, setEditingBilling] = useState(null);
  const [newBillingDate, setNewBillingDate] = useState('');

  useEffect(() => { fetchClientData(); }, [client.id]);

  const fetchClientData = async () => {
    setLoading(true);
    try {
      // Fetch subscriptions
      const { data: subs } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('client_id', client.id)
        .order('created_at', { ascending: false });

      // Fetch products separately
      if (subs && subs.length > 0) {
        const productIds = [...new Set(subs.filter(s => s.product_id).map(s => s.product_id))];
        if (productIds.length > 0) {
          const { data: products } = await supabase
            .from('products')
            .select('id, name, price_amount, billing_interval, billing_type, currency')
            .in('id', productIds);
          const merged = subs.map(sub => ({
            ...sub,
            product: (products || []).find(p => p.id === sub.product_id) || null
          }));
          setSubscriptions(merged);
        } else {
          setSubscriptions(subs);
        }
      } else {
        setSubscriptions([]);
      }

      // Fetch ALL transactions for this client
      const { data: txns } = await supabase
        .from('transactions')
        .select('*')
        .eq('client_id', client.id)
        .order('created_at', { ascending: false });
      setTransactions(txns || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSub = async (sub) => {
    if (!window.confirm('Cancel this subscription?')) return;
    setCancelling(sub.id);
    try {
      await supabase.from('subscriptions').update({ status: 'cancelled' }).eq('id', sub.id);
      fetchClientData();
    } catch (err) {
      alert(err.message);
    } finally {
      setCancelling(null);
    }
  };

  const handleUpdateBilling = async (sub) => {
    if (!newBillingDate) return;
    await supabase.from('subscriptions')
      .update({ current_period_end: new Date(newBillingDate).toISOString() })
      .eq('id', sub.id);
    setEditingBilling(null);
    setNewBillingDate('');
    fetchClientData();
  };

  const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
  const intervalLabel = { week: 'Weekly', biweek: 'Bi-weekly', month: 'Monthly', year: 'Yearly' };
  const totalPaid = transactions.reduce((s, t) => s + Number(t.gross_amount || 0), 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-light)',
        borderRadius: 16, width: '100%', maxWidth: 640,
        maxHeight: '90vh', overflowY: 'auto', padding: 32
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <h3 style={{ fontSize: 22, marginBottom: 4 }}>{client.full_name}</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{client.email}</p>
            {client.phone && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{client.phone}</p>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm"
              onClick={async () => {
                try {
                  const { error } = await supabase.auth.resetPasswordForEmail(client.email, {
                    redirectTo: window.location.origin + '/set-password'
                  });
                  if (error) throw error;
                  alert(`Password reset email sent to ${client.email}`);
                } catch (err) {
                  alert('Could not send: ' + err.message);
                }
              }}>
              Send Reset Email
            </button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer' }}>✕</button>
          </div>
        </div>

        {/* Summary */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 28 }}>
          {[
            { label: 'Total Paid', value: fmt(totalPaid) },
            { label: 'Active Subs', value: subscriptions.filter(s => s.status === 'active').length },
            { label: 'Payments', value: transactions.length }
          ].map((s, i) => (
            <div key={i} style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{s.label}</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {loading ? <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: 20 }}>Loading...</div> : (
          <>
            {/* Subscriptions */}
            <h4 style={{ fontSize: 15, fontFamily: 'DM Sans', fontWeight: 600, marginBottom: 12 }}>Subscriptions</h4>
            {subscriptions.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>No subscriptions.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
                {subscriptions.map(sub => (
                  <div key={sub.id} style={{
                    background: 'var(--bg-secondary)', borderRadius: 8,
                    padding: '14px 16px', border: '1px solid var(--border)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 500, marginBottom: 2 }}>{sub.product?.name || 'Subscription'}</div>
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                          {sub.product?.price_amount ? fmt(sub.product.price_amount) : '—'}
                          {sub.product?.billing_type !== 'one_time' && sub.product?.billing_interval
                            ? ` · ${intervalLabel[sub.product.billing_interval]}`
                            : ''}
                        </div>
                      </div>
                      <span className={`badge badge-${sub.status === 'active' ? 'active' : 'inactive'}`}>{sub.status}</span>
                    </div>

                    {/* Next billing date */}
                    {sub.status === 'active' && (
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
                        Next billing: {sub.current_period_end
                          ? new Date(sub.current_period_end).toLocaleDateString('en-CA')
                          : '—'}
                        {sub.status === 'active' && (
                          <span
                            onClick={() => {
                              setEditingBilling(sub.id);
                              setNewBillingDate(sub.current_period_end
                                ? new Date(sub.current_period_end).toISOString().split('T')[0]
                                : '');
                            }}
                            style={{ color: 'var(--accent)', cursor: 'pointer', marginLeft: 8, fontSize: 12 }}>
                            edit date
                          </span>
                        )}
                      </div>
                    )}

                    {editingBilling === sub.id && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                        <input type="date" className="input" value={newBillingDate}
                          onChange={e => setNewBillingDate(e.target.value)}
                          style={{ width: 140, fontSize: 13 }} />
                        <button className="btn btn-primary btn-sm" onClick={() => handleUpdateBilling(sub)}>Save</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setEditingBilling(null)}>Cancel</button>
                      </div>
                    )}

                    {sub.status === 'active' && (
                      <button className="btn btn-danger btn-sm"
                        onClick={() => handleCancelSub(sub)}
                        disabled={cancelling === sub.id}>
                        {cancelling === sub.id ? 'Cancelling...' : 'Cancel Subscription'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Payment history */}
            <h4 style={{ fontSize: 15, fontFamily: 'DM Sans', fontWeight: 600, marginBottom: 12 }}>Payment History</h4>
            {transactions.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>No payments recorded.</div>
            ) : (
              <table className="table" style={{ fontSize: 13 }}>
                <thead>
                  <tr><th>Description</th><th>Amount</th><th>Status</th><th>Date</th></tr>
                </thead>
                <tbody>
                  {transactions.map(t => (
                    <tr key={t.id}>
                      <td>{t.description || 'Payment'}</td>
                      <td style={{ fontWeight: 500 }}>{fmt(t.gross_amount)}</td>
                      <td>
                        <span className={`badge badge-${t.status === 'completed' ? 'active' : t.status === 'refunded' ? 'inactive' : 'pending'}`}>
                          {t.status}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text-muted)' }}>{new Date(t.created_at).toLocaleDateString('en-CA')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Clients Page ─────────────────────────────────────────────────────────
export default function Clients() {
  const { profile } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (profile?.id) fetchClients();
  }, [profile]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      // Always show only MY clients (coach_id = me)
      const { data } = await supabase
        .from('clients')
        .select('*, subscriptions(id, status)')
        .eq('coach_id', profile.id)
        .order('created_at', { ascending: false });
      setClients(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = clients.filter(c => {
    const matchSearch = !search ||
      c.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' ||
      (filter === 'active' && c.subscriptions?.some(s => s.status === 'active')) ||
      (filter === 'inactive' && !c.subscriptions?.some(s => s.status === 'active'));
    return matchSearch && matchFilter;
  });

  const activeCount = clients.filter(c => c.subscriptions?.some(s => s.status === 'active')).length;

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, marginBottom: 8 }}>My Clients</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          {clients.length} client{clients.length !== 1 ? 's' : ''} · {activeCount} with active subscriptions
        </p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: `All (${clients.length})` },
          { key: 'active', label: `Active (${activeCount})` },
          { key: 'inactive', label: `Inactive (${clients.length - activeCount})` }
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={filter === f.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
            {f.label}
          </button>
        ))}
        <input className="input" placeholder="Search clients..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 220, marginLeft: 'auto' }} />
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, color: 'var(--text-secondary)' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
            {search || filter !== 'all'
              ? 'No clients match.'
              : 'No clients yet. Clients appear here automatically when they pay through your payment links.'}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Active Subscriptions</th>
                <th>Status</th>
                <th>Since</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const activeSubs = c.subscriptions?.filter(s => s.status === 'active').length || 0;
                return (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedClient(c)}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{c.full_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.email}</div>
                    </td>
                    <td>
                      <span style={{
                        background: activeSubs > 0 ? 'rgba(76,175,130,0.12)' : 'rgba(90,85,80,0.2)',
                        color: activeSubs > 0 ? 'var(--success)' : 'var(--text-muted)',
                        padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500
                      }}>
                        {activeSubs} active
                      </span>
                    </td>
                    <td><span className={`badge badge-${c.status}`}>{c.status}</span></td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      {new Date(c.created_at).toLocaleDateString('en-CA')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selectedClient && (
        <ClientProfile client={selectedClient} onClose={() => setSelectedClient(null)} />
      )}
    </div>
  );
}
